import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Card,
  CardContent,
  CardActions,
  Autocomplete,
  Breadcrumbs,
  Link
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import GuideButton from '../../common/guide/GuideButton';
import N11ProductCreateModal from './N11ProductCreateModal';
import { n11Api } from '../../../config/apiClient';

// N11 Katalog Ürünü Arayüzü
export interface N11CatalogProduct {
  catalogId: string;
  categoryId: string;
  categoryName: string;
  productTitle: string;
  imageUrl?: string | null;
  n11Url?: string;
}

// Arama Sonucu Arayüzü
interface SearchResponse {
  products: N11CatalogProduct[];
  searchRequest: {
    catalogId?: string;
    productTitle?: string;
    categoryId?: string;
    brandName?: string;
  };
}

// N11 Kategori Arayüzü
interface N11Category {
  id: string;
  name: string;
  fullPath?: string;
  level?: number;
  hasChildren?: boolean;
}

// Props Arayüzü
interface N11ProductUploadModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null;
  onSaveLog?: (logEntry: {
    type: 'price-stock-update' | 'product-create' | 'product-save';
    productName: string;
    stockCode: string;
    taskId: number;
    status: 'IN_QUEUE' | 'PROCESSED' | 'REJECT';
    operation: string;
    details: any;
  }) => void;
}

const N11ProductUploadModal: React.FC<N11ProductUploadModalProps> = ({
  open,
  onClose,
  companyId,
  onSaveLog
}) => {
  // State değişkenleri
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<SearchResponse[]>([]);
  

  
  // Form state'leri
  const [productTitleInput, setProductTitleInput] = useState<string>('');
  const [categoryIdInput, setCategoryIdInput] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<N11Category | null>(null);
  const [categories, setCategories] = useState<N11Category[]>([]);
  const [categorySearchTerm, setCategorySearchTerm] = useState<string>('');
  const [categoryLoading, setCategoryLoading] = useState<boolean>(false);
  const [categoryBreadcrumb, setCategoryBreadcrumb] = useState<N11Category[]>([]);
  const [currentParentId, setCurrentParentId] = useState<string | null>(null);
  
  // Ürün oluşturma modal'ı state'leri
  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<N11CatalogProduct | null>(null);
  
  // Sadece ürün adı ile arama yapacağız

  // Modal açıldığında form'u temizle ve ana kategorileri yükle
  useEffect(() => {
    if (open) {
      handleClearForm();
      loadMainCategories();
    }
  }, [open]);

  // Tüm kategorileri yükle
  // Ana kategorileri yükle (modal açıldığında)
  const loadMainCategories = async () => {
    if (!companyId) return;

    setCategoryLoading(true);
    try {
      console.log('🏠 [N11 CATEGORIES] Ana kategoriler yükleniyor...');

      const response = await n11Api.post('/api/n11/products/search-categories', { 
        searchTerm: '', 
        parentId: null
      });

      const data = response.data;
      
      if (!data.success) {
        throw new Error(data.error || 'Ana kategori yükleme işlemi başarısız');
      }

      console.log('✅ [N11 CATEGORIES] Ana kategoriler yüklendi:', data.categories?.length || 0);
      setCategories(data.categories || []);
      setCategoryBreadcrumb([]);
      setCurrentParentId(null);

    } catch (error) {
      console.error('❌ [N11 CATEGORIES] Ana kategori yükleme hatası:', error);
      setError('Ana kategoriler yüklenirken hata oluştu: ' + (error instanceof Error ? error.message : 'Bilinmeyen hata'));
    } finally {
      setCategoryLoading(false);
    }
  };

  // Alt kategorileri yükle (kategori seçildiğinde)
  const loadSubCategories = async (parentCategory: N11Category) => {
    if (!companyId) return;

    setCategoryLoading(true);
    try {
      console.log('🌳 [N11 CATEGORIES] Alt kategoriler yükleniyor...', parentCategory.name);

      const response = await n11Api.post('/api/n11/products/search-categories', { 
        searchTerm: '', 
        parentId: parentCategory.id
      });

      const data = response.data;
      
      if (!data.success) {
        throw new Error(data.error || 'Alt kategori yükleme işlemi başarısız');
      }

      console.log('✅ [N11 CATEGORIES] Alt kategoriler yüklendi:', data.categories?.length || 0);
      
      if (data.categories && data.categories.length > 0) {
        // Alt kategoriler var, bunları göster
        setCategories(data.categories);
        setCategoryBreadcrumb([...categoryBreadcrumb, parentCategory]);
        setCurrentParentId(parentCategory.id);
      } else {
        // Alt kategori yok, bu kategoriyi seç
        setSelectedCategory(parentCategory);
        setCategoryIdInput(parentCategory.id);
        console.log('📄 [N11 CATEGORIES] Son seviye kategori seçildi:', parentCategory.name);
      }

    } catch (error) {
      console.error('❌ [N11 CATEGORIES] Alt kategori yükleme hatası:', error);
      setError('Alt kategoriler yüklenirken hata oluştu: ' + (error instanceof Error ? error.message : 'Bilinmeyen hata'));
    } finally {
      setCategoryLoading(false);
    }
  };

  // Form temizleme
  const handleClearForm = () => {
    setProductTitleInput('');
    setCategoryIdInput('');
    setSelectedCategory(null);
    setCategories([]);
    setCategorySearchTerm('');
    setCategoryBreadcrumb([]);
    setCurrentParentId(null);
    setSearchResults([]);
    setError(null);
  };

  // Global kategori arama (tüm kategorilerde arama)
  const searchCategories = async (searchTerm: string) => {
    if (!companyId) return;

    setCategoryLoading(true);
    try {
      console.log('🔍 [N11 CATEGORY SEARCH] Global kategori arama:', searchTerm);

      const response = await n11Api.post('/api/n11/products/search-categories', { 
        searchTerm,
        parentId: null
      });

      const data = response.data;
      
      if (!data.success) {
        throw new Error(data.error || 'Kategori arama işlemi başarısız');
      }

      console.log('✅ [N11 CATEGORY SEARCH] Arama sonuçları:', data.categories?.length || 0);
      setCategories(data.categories || []);

    } catch (error) {
      console.error('❌ [N11 CATEGORY SEARCH] Kategori arama hatası:', error);
      setError('Kategori arama hatası: ' + (error instanceof Error ? error.message : 'Bilinmeyen hata'));
    } finally {
      setCategoryLoading(false);
    }
  };

  // Breadcrumb'da geri gitme
  const goBackToParent = (targetIndex: number) => {
    if (targetIndex === -1) {
      // Ana kategorilere dön
      loadMainCategories();
    } else {
      // Belirli bir seviyeye dön
      const targetParent = categoryBreadcrumb[targetIndex];
      loadSubCategories(targetParent);
    }
  };

  // N11 Katalog Arama
  const handleSearchCatalog = async () => {
    if (!companyId) {
      setError('Şirket bilgisi bulunamadı');
      return;
    }

    // Ürün adı kontrolü
    if (!productTitleInput.trim()) {
      setError('Lütfen ürün adı giriniz (en az 10 karakter)');
      return;
    }
    if (productTitleInput.trim().length < 10) {
      setError('Ürün adı en az 10 karakter olmalıdır');
      return;
    }

    // Kategori seçimi kontrolü
    if (!selectedCategory) {
      setError('Kategori seçimi zorunludur');
      return;
    }

    // Arama parametrelerini hazırla
    const params = {
      productTitles: [productTitleInput.trim()],
      categoryId: selectedCategory.id
    };

    setLoading(true);
    setError(null);
    
    try {
      console.log('🔍 [N11 CATALOG SEARCH] Arama başlatılıyor:', params);
      
      // Backend API'ye istek gönder
      const response = await n11Api.post('/api/n11/products/search-catalog', params);

      const data = response.data;
      
      if (!data.success) {
        throw new Error(data.error || 'Katalog arama işlemi başarısız');
      }

      console.log('✅ [N11 CATALOG SEARCH] Arama sonuçları:', data.searchResponses);
      setSearchResults(data.searchResponses || []);
      
      // Toplam ürün sayısını hesapla
      const totalProducts = (data.searchResponses || []).reduce((total: number, response: any) => {
        return total + (response.products?.length || 0);
      }, 0);
      
      console.log(`📊 [N11 CATALOG SEARCH] Toplam ${totalProducts} ürün bulundu`);
      
      if (totalProducts === 0) {
        setError(`"${productTitleInput}" için "${selectedCategory?.fullPath || selectedCategory?.name}" kategorisinde ürün bulunamadı.
        
Öneriler:
• Farklı bir kategori seçin (örn: "Elektronik > Bilgisayar > Aksesuar & Yedek Parça > Kablolar")
• Ürün adını değiştirin (örn: "hdmi kablo" yerine "hdmi cable")
• Daha genel bir terim kullanın`);
      }
      
    } catch (error) {
      console.error('❌ [N11 CATALOG SEARCH] Arama hatası:', error);
      setError(error instanceof Error ? error.message : 'Katalog arama sırasında bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };



  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: 'primary.main',
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CloudUploadIcon />
          <Typography variant="h6">N11 Ürün Yükleme</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GuideButton guideKey="inventory.n11Upload" size="small" color="inherit" disabled={loading} />
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            disabled={loading}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Kompakt Arama Formu */}
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                size="small"
                label="N11'de Aranacak Ürün Adı"
                value={productTitleInput}
                onChange={(e) => setProductTitleInput(e.target.value)}
                placeholder="Aranacak ürün adını giriniz (en az 10 karakter)"
                disabled={loading}
                helperText="En az 10 karakter giriniz"
                error={productTitleInput.length > 0 && productTitleInput.length < 10}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              {/* Breadcrumb Navigasyon */}
              {categoryBreadcrumb.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Breadcrumbs aria-label="kategori-breadcrumb" sx={{ fontSize: '0.8rem' }}>
                    <Link
                      component="button"
                      variant="caption"
                      onClick={() => goBackToParent(-1)}
                      sx={{ textDecoration: 'none', cursor: 'pointer' }}
                    >
                      🏠 Ana Kategoriler
                    </Link>
                    {categoryBreadcrumb.map((category, index) => (
                      <Link
                        key={category.id}
                        component="button"
                        variant="caption"
                        onClick={() => goBackToParent(index)}
                        sx={{ textDecoration: 'none', cursor: 'pointer' }}
                      >
                        📁 {category.name}
                      </Link>
                    ))}
                    <Typography variant="caption" color="text.primary">
                      📂 Mevcut Seviye
                    </Typography>
                  </Breadcrumbs>
                </Box>
              )}
              
              <Autocomplete
                fullWidth
                size="small"
                options={categories}
                getOptionLabel={(option) => {
                  const hasChildrenIcon = option.hasChildren ? '📁' : '📄';
                  return `${hasChildrenIcon} ${option.name}`;
                }}
                value={selectedCategory}
                onInputChange={(event, newInputValue, reason) => {
                  setCategorySearchTerm(newInputValue);
                  // Sadece kullanıcı klavyeden yazarken backend araması yap.
                  // Option seçimi sonrası oluşan "reset" event'i seçimi bozmasın.
                  if (reason === 'input' && newInputValue.length >= 2) {
                    searchCategories(newInputValue);
                  } else if (reason === 'clear' || (reason === 'input' && newInputValue.length === 0)) {
                    // Boş arama = mevcut seviyeye dön
                    if (currentParentId) {
                      const currentParent = categoryBreadcrumb[categoryBreadcrumb.length - 1];
                      if (currentParent) {
                        loadSubCategories(currentParent);
                      }
                    } else {
                      loadMainCategories();
                    }
                  }
                }}
                onChange={(event, newValue) => {
                  if (newValue) {
                    // Kategori seçildi: seçimi hemen state'e yaz.
                    // Alt kategori varsa yine listelensin ama seçim kaybolmasın.
                    setSelectedCategory(newValue);
                    setCategoryIdInput(newValue.id);
                    loadSubCategories(newValue);
                  } else {
                    setSelectedCategory(null);
                    setCategoryIdInput('');
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={selectedCategory ? `Seçilen: ${selectedCategory.name}` : "Kategori Seç (Zorunlu)"}
                    placeholder={categoryBreadcrumb.length > 0 ? "Alt kategori seçin..." : "Ana kategori seçin..."}
                    disabled={loading}
                    helperText={selectedCategory ? `Kategori ID: ${selectedCategory.id}` : "Kategori seçin veya arama yapın"}
                    error={productTitleInput.length >= 10 && !selectedCategory}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {categoryLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                disabled={loading}
                loading={categoryLoading}
                noOptionsText={categorySearchTerm.length < 2 ? "En az 2 karakter yazın" : "Kategori bulunamadı"}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(x) => x} // Filtrelemeyi backend'e bırak
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                fullWidth
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                onClick={handleSearchCatalog}
                disabled={loading || productTitleInput.length < 10 || !selectedCategory}
                sx={{ height: '40px' }}
              >
                {loading ? 'Aranıyor...' : 'Ara'}
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Kategori durumu (açıklama rehberde) */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {categoryLoading ? 'Kategoriler yükleniyor...' : `${categories.length} kategori mevcut seviyede`}
        </Typography>

        {/* Hata Mesajı */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Arama Sonuçları */}
        {searchResults.length > 0 && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 1, color: 'primary.main' }}>
              🔍 Arama Sonuçları ({searchResults.reduce((total, response) => total + (response.products?.length || 0), 0)} ürün bulundu)
            </Typography>
            
            <Box sx={{ flex: 1, overflowY: 'auto', pr: 1 }}>
              {searchResults.map((response, responseIndex) => (
                <Box key={responseIndex}>
                  {response.products && response.products.length > 0 ? (
                    response.products.map((product, productIndex) => (
                      <Card 
                        key={`${responseIndex}-${productIndex}`} 
                        variant="outlined" 
                        sx={{ 
                          mb: 2, 
                          '&:hover': { 
                            boxShadow: 2,
                            borderColor: 'primary.main'
                          },
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >
                        <CardContent sx={{ pb: 1 }}>
                          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            {/* Ürün Resmi */}
                            <Box sx={{ 
                              width: 80, 
                              height: 80, 
                              flexShrink: 0,
                              borderRadius: 1,
                              overflow: 'hidden',
                              bgcolor: 'grey.100',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '1px solid',
                              borderColor: 'grey.300'
                            }}>
                              {product.imageUrl ? (
                                <img 
                                  src={product.imageUrl} 
                                  alt={product.productTitle}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                  }}
                                  onError={(e) => {
                                    // Resim yüklenemezse placeholder göster
                                    e.currentTarget.style.display = 'none';
                                    const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                                    if (nextElement) {
                                      nextElement.style.display = 'flex';
                                    }
                                  }}
                                />
                              ) : null}
                              <Box sx={{ 
                                display: product.imageUrl ? 'none' : 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '100%',
                                height: '100%',
                                color: 'grey.500'
                              }}>
                                <CloudUploadIcon />
                              </Box>
                            </Box>
                            
                            {/* Ürün Bilgileri */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                <Typography variant="h6" fontWeight="bold" color="primary.main" sx={{ flex: 1, mr: 2 }}>
                                  {product.productTitle}
                                </Typography>
                                <Chip
                                  label={`ID: ${product.catalogId}`}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              </Box>
                              
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Chip
                                  label={product.categoryName}
                                  size="small"
                                  color="secondary"
                                  variant="filled"
                                />
                                <Typography variant="caption" color="text.secondary">
                                  Kategori ID: {product.categoryId}
                                </Typography>
                              </Box>
                              
                              {/* N11 Link */}
                              {product.n11Url && (
                                <Typography variant="caption" color="primary.main" sx={{ cursor: 'pointer' }}>
                                  <a href={product.n11Url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                                    🔗 N11'de Görüntüle
                                  </a>
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </CardContent>
                        
                        <CardActions sx={{ pt: 0, justifyContent: 'flex-end', bgcolor: 'grey.50' }}>
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={<CloudUploadIcon />}
                            onClick={() => {
                              setSelectedProduct(product);
                              setCreateModalOpen(true);
                            }}
                            sx={{ fontWeight: 'bold' }}
                          >
                            Sisteme Ekle
                          </Button>
                        </CardActions>
                      </Card>
                    ))
                  ) : null}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Sonuç Bulunamadı Mesajı */}
        {!loading && searchResults.length === 0 && !error && (
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center', 
            alignItems: 'center',
            textAlign: 'center',
            py: 4
          }}>
            <SearchIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              N11 Katalog Arama
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Yukarıdaki alana ürün adı yazarak N11 katalogunda arama yapabilirsiniz.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
        <Button
          onClick={handleClearForm}
          disabled={loading}
          color="inherit"
        >
          Temizle
        </Button>
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
        >
          Kapat
        </Button>
      </DialogActions>

      {/* N11 Ürün Oluşturma Modal'ı */}
      <N11ProductCreateModal
        open={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          setSelectedProduct(null);
        }}
        product={selectedProduct}
        companyId={companyId}
        onSaveLog={onSaveLog}
      />
    </Dialog>
  );
};

export default N11ProductUploadModal;
